import { CORE_ROLES } from '@/components/authnz/types/rbac';
import { getProblemKind } from './problem-filter';
import type { EmailKeyUserRow, ProblemFilter } from './types';

export const USERS_ROLES_SORT_COLUMNS = [
  'emailKey',
  'displayName',
  'grants',
  'lifecycle',
] as const;

export type UsersRolesSortColumn = (typeof USERS_ROLES_SORT_COLUMNS)[number];
export type UsersRolesSortDirection = 'asc' | 'desc';

const LIFECYCLE_ORDER: ProblemFilter[] = [
  'existing',
  'invited-not-signed-in',
  'fallback',
  'auth-disabled-without-deleted',
  'deleted',
];

export function grantSortRank(row: Pick<EmailKeyUserRow, 'primaryRole' | 'roles'>): number {
  const role = row.primaryRole ?? row.roles[0] ?? null;
  if (!role) {
    return CORE_ROLES.length;
  }
  const index = CORE_ROLES.indexOf(role as (typeof CORE_ROLES)[number]);
  return index === -1 ? CORE_ROLES.length : index;
}

export function lifecycleSortRank(row: EmailKeyUserRow): number {
  const kind = getProblemKind(row);
  if (!kind || kind === 'all') {
    return LIFECYCLE_ORDER.length;
  }
  const index = LIFECYCLE_ORDER.indexOf(kind);
  return index === -1 ? LIFECYCLE_ORDER.length : index;
}

function compareStrings(left: string, right: string): number {
  return left.localeCompare(right, undefined, { sensitivity: 'base' });
}

export function compareUsersRolesRows(
  left: EmailKeyUserRow,
  right: EmailKeyUserRow,
  column: UsersRolesSortColumn,
): number {
  switch (column) {
    case 'emailKey':
      return compareStrings(left.emailKey, right.emailKey);
    case 'displayName':
      return (
        compareStrings(left.displayName || left.emailKey, right.displayName || right.emailKey) ||
        compareStrings(left.emailKey, right.emailKey)
      );
    case 'grants': {
      const rank = grantSortRank(left) - grantSortRank(right);
      if (rank !== 0) {
        return rank;
      }
      const count = left.roles.length - right.roles.length;
      if (count !== 0) {
        return count;
      }
      return compareStrings(left.emailKey, right.emailKey);
    }
    case 'lifecycle': {
      const rank = lifecycleSortRank(left) - lifecycleSortRank(right);
      if (rank !== 0) {
        return rank;
      }
      return compareStrings(left.emailKey, right.emailKey);
    }
    default:
      return compareStrings(left.emailKey, right.emailKey);
  }
}

export function sortUsersRolesRows(
  rows: EmailKeyUserRow[],
  column: UsersRolesSortColumn,
  direction: UsersRolesSortDirection,
): EmailKeyUserRow[] {
  const factor = direction === 'desc' ? -1 : 1;
  return [...rows].sort((left, right) => factor * compareUsersRolesRows(left, right, column));
}

export function nextUsersRolesSort(
  currentColumn: UsersRolesSortColumn,
  currentDirection: UsersRolesSortDirection,
  clicked: UsersRolesSortColumn,
): { column: UsersRolesSortColumn; direction: UsersRolesSortDirection } {
  if (currentColumn === clicked) {
    return { column: clicked, direction: currentDirection === 'asc' ? 'desc' : 'asc' };
  }
  return { column: clicked, direction: 'asc' };
}
